import { useEffect } from 'react'
import { TopNav, Footer } from '../components/PublicLayout'
import SEO from '../components/SEO'

// ─── Terms & conditions ──────────────────────────────────────────────────────
//
// IMPORTANT: This is a reasonable starting template, not legal advice. Before
// taking real payments from real customers, have a UK solicitor review and
// customise this, or use a service like Termly / Iubenda / SeedLegals.
//
// Editable values — update these and the rest of the document picks them up.
const LAST_UPDATED  = '18 May 2026'
const CONTACT_EMAIL = 'hello@receiptchaser.com'
const TRADER_NAME   = 'John Smith'
const TRADING_AS    = 'Receipt Chaser'
const TRIAL_DAYS    = 30
const JURISDICTION  = 'England and Wales'

// ─── Page-section components (shared styling for headings, body, etc.) ───────

function Section({ number, title, children }) {
  return (
    <section style={{ marginBottom: 36 }}>
      <h2 style={{
        fontSize: '1.25rem', fontFamily: 'var(--font-display)',
        fontWeight: 400, marginBottom: 14, lineHeight: 1.3,
      }}>
        {number}. {title}
      </h2>
      <div style={{ fontSize: '0.95rem', color: 'var(--ink-muted)', lineHeight: 1.7 }}>
        {children}
      </div>
    </section>
  )
}

function P({ children }) {
  return <p style={{ marginBottom: 12 }}>{children}</p>
}

function UL({ children }) {
  return (
    <ul style={{
      paddingLeft: 22, marginBottom: 12,
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      {children}
    </ul>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function Terms() {
  // Scroll to top on mount (useful when navigated to from a footer link
  // on a long page like Home).
  useEffect(() => { window.scrollTo(0, 0) }, [])

  return (
    <div style={{ background: 'var(--surface)', minHeight: '100vh' }}>
      <SEO
        title="Terms and conditions"
        description="The terms and conditions governing the use of Receipt Chaser, the receipt collection service for UK bookkeeping practices."
        path="/terms"
      />
      <TopNav />

      <main style={{ padding: '48px 0 64px' }}>
        <div className="container" style={{
          padding: '0 24px', maxWidth: 760, margin: '0 auto',
        }}>
          <header style={{ marginBottom: 40 }}>
            <h1 style={{
              fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
              marginBottom: 8, lineHeight: 1.2,
            }}>
              Terms &amp; Conditions
            </h1>
            <p style={{ color: 'var(--ink-faint)', fontSize: '0.85rem' }}>
              Last updated: {LAST_UPDATED}
            </p>
          </header>

          <div style={{
            background: 'var(--white)', borderRadius: 'var(--radius-lg)',
            padding: 'clamp(28px, 5vw, 48px)',
            border: '1px solid var(--surface-2)',
          }}>

            <Section number="1" title="Who we are">
              <P>
                Receipt Chaser ("we", "us", "our") is a software service
                provided by Waterman Bookkeeping, trading as "Receipt Chaser"
                in the United Kingdom.
              </P>
              <P>
                These terms govern your use of the website, app and services
                we provide at receiptchaser.com (the "Service"). By creating
                an account or otherwise using the Service, you agree to these
                terms. If you don't agree, please don't use the Service.
              </P>
              <P>
                You can contact us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
              </P>
            </Section>

            <Section number="2" title="What the service does">
              <P>
                Receipt Chaser helps bookkeepers and accountants request
                receipts from their clients. The bookkeeper uploads a list of
                transactions, sends a link to their client, and the client
                uploads the matching receipts. The bookkeeper can then
                download those receipts and a summary of the request.
              </P>
              <P>
                We don't provide bookkeeping, accounting, tax, or financial
                advice. We're a tool that bookkeepers use as part of their
                own service to their clients. You're responsible for the
                accuracy of the information you and your clients enter, and
                for how that information is used in your bookkeeping.
              </P>
            </Section>

            <Section number="3" title="Your account">
              <P>
                To use most parts of the Service you need a bookkeeper
                account. You must be at least 18 years old, have the legal
                authority to enter into a contract, and provide accurate
                information when signing up.
              </P>
              <P>
                You're responsible for keeping your login credentials secure,
                for any activity that happens under your account, and for
                making sure anyone you invite into your practice on the
                Service has appropriate authority to access the information
                they'll see.
              </P>
              <P>
                Your clients do not need an account. They access their request
                via a private link you send them. You're responsible for who
                you share those links with.
              </P>
            </Section>

            <Section number="4" title="Free trial">
              <P>
                New bookkeeper accounts include a free trial period of {TRIAL_DAYS} days
                from the date the account is created. During the trial you have
                full access to the Service. No payment method is required to
                start a trial.
              </P>
              <P>
                At the end of the trial, you'll need to choose a paid plan to
                continue using the Service. If you don't, your account will be
                locked but your data will be retained per section 9 below.
              </P>
            </Section>

            <Section number="5" title="Pricing and payment">
              <P>
                Pricing for our paid plans is shown on our{' '}
                <a href="/pricing">pricing page</a>. All prices are in pounds
                sterling and are exclusive of any VAT that may apply.
              </P>
              <P>
                Subscriptions are billed in advance on a recurring monthly
                basis to the payment method on file. By subscribing, you
                authorise us (via our payment processor) to charge that
                payment method for each billing period until you cancel.
              </P>
              <P>
                We may change our prices from time to time. If we do, we'll
                give you at least 30 days' notice by email before the change
                affects your subscription. If you don't agree to a price
                change, you can cancel before it takes effect.
              </P>
            </Section>

            <Section number="6" title="Cancellation and refunds">
              <P>
                You can cancel your subscription at any time from your
                account settings. Cancellation takes effect at the end of the
                current billing period &mdash; you keep access until then.
              </P>
              <P>
                <strong>We do not offer pro-rata refunds for unused portions
                of the current billing period.</strong> If you cancel mid-month,
                you keep access until the end of that month, but won't be
                charged for the next one.
              </P>
              <P>
                If you believe you've been charged in error, contact us at{' '}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and
                we'll investigate.
              </P>
            </Section>

            <Section number="7" title="Acceptable use">
              <P>
                You agree not to use the Service to:
              </P>
              <UL>
                <li>Upload anything illegal, infringing, defamatory, or harmful;</li>
                <li>Collect or upload personal data you don't have the right to share;</li>
                <li>Attempt to access another user's account or data;</li>
                <li>Reverse-engineer, scrape, or build a competing service from ours;</li>
                <li>Interfere with the operation of the Service, or attempt to overwhelm or attack it;</li>
                <li>Use the Service in any way that breaks UK law or any applicable regulation.</li>
              </UL>
              <P>
                We may suspend or terminate accounts that breach these rules.
                Where we believe a serious breach has occurred we may do so
                without notice.
              </P>
            </Section>

            <Section number="8" title="Your data and your clients' data">
              <P>
                You and your clients keep ownership of all the information,
                receipts, and files uploaded to the Service. By using the
                Service, you grant us a limited right to store and process
                that information solely so we can provide the Service to you.
              </P>
              <P>
                We don't sell your data. We don't share your clients'
                financial information with advertisers. We don't use your
                content to train machine learning models. We use it to make
                the Service work for you.
              </P>
              <P>
                More detail on how we handle personal data is in our{' '}
                <a href="/privacy">Privacy Policy</a>.
              </P>
            </Section>

            <Section number="9" title="Data retention">
              <P>
                <strong>While your account is active</strong>, we
                retain each uploaded file for 7 years from the date
                the file was uploaded. This aligns with HMRC
                record-keeping requirements for UK businesses and
                ensures you can access historical records during that
                window. The 7-year clock runs from the upload date
                itself, so files reach the end of their retention
                period regardless of how long your subscription
                continues.
              </P>
              <P>
                <strong>When your access ends</strong> (your
                subscription is cancelled and the paid period has
                run out, or your free trial elapsed without you
                subscribing), we keep your account and data for a
                further <strong>90 days</strong>. You can resubscribe
                at any time within that 90-day window to restore full
                access. We'll send you a final warning email roughly
                7 days before deletion.
              </P>
              <P>
                After the 90 days, your account is{' '}
                <strong>permanently deleted</strong>: receipt files,
                clients, requests, transactions, and account settings.
                The 90-day post-closure policy overrides the 7-year
                file retention. Once an account is deleted, its files
                are deleted with it. None of this can be undone once
                it has happened.
              </P>
              <P>
                You can ask us to delete your files or your whole
                account sooner by emailing{' '}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                Where we can, we'll honour that request, subject to
                any legal obligations that require us to retain
                certain records.
              </P>
              <P>
                If a file reaches 7 years from its upload date while
                your account is still active, we mark the related
                request as scheduled for deletion and hide it from
                your view. The receipt files themselves are permanently
                deleted 30 days later. The underlying transaction
                record (date, description, amount) is preserved as
                historical metadata; only the receipt files and any
                client explanations are removed.
              </P>
            </Section>

            <Section number="10" title="Availability">
              <P>
                We aim to keep the Service available and working well, but we
                don't guarantee any specific level of uptime. From time to
                time the Service may be unavailable for maintenance, upgrades,
                or due to events outside our control.
              </P>
              <P>
                We may also change features, add new ones, or remove old ones.
                If we discontinue the Service entirely, we'll give you
                reasonable notice and an opportunity to download your data.
              </P>
            </Section>

            <Section number="11" title="Our liability to you">
              <P>
                We do everything reasonable to provide a reliable Service.
                However, nothing on this site or in the Service should be
                taken as a guarantee of any particular outcome. You and your
                clients are responsible for the information you enter, and
                for how it's used in your bookkeeping or financial reporting.
              </P>
              <P>
                To the extent permitted by law, we are not liable for: lost
                profits or revenue; loss of data caused by your actions or
                those of your clients; or any indirect, incidental,
                consequential, or special damages arising from your use of
                the Service.
              </P>
              <P>
                Where the law does require us to be liable, our total
                liability to you for any claim arising from these terms or
                your use of the Service is limited to the amount you've paid
                us in the 12 months before the claim arose.
              </P>
              <P>
                Nothing in these terms limits or excludes our liability for
                death or personal injury caused by our negligence, for fraud,
                or for anything else that can't be limited or excluded under
                UK law.
              </P>
            </Section>

            <Section number="12" title="Termination">
              <P>
                You can stop using the Service and cancel your account at any
                time.
              </P>
              <P>
                We may suspend or terminate your access if you breach these
                terms, fail to pay for the Service, or use the Service in a
                way that puts us, our other users, or our infrastructure at
                risk. Where reasonable we'll give you notice and a chance to
                fix the problem.
              </P>
              <P>
                On termination, sections 8 (data ownership), 9 (data
                retention), 11 (liability), and 14 (governing law) continue
                to apply.
              </P>
            </Section>

            <Section number="13" title="Changes to these terms">
              <P>
                We may update these terms occasionally &mdash; for example, to
                reflect changes to the law, to our pricing model, or to the
                Service itself.
              </P>
              <P>
                If we make material changes, we'll let you know by email
                before they take effect, and update the "Last updated" date
                at the top of this page. Continuing to use the Service after
                the changes take effect means you accept the updated terms.
                If you don't accept them, you can cancel under section 6.
              </P>
            </Section>

            <Section number="14" title="Governing law">
              <P>
                These terms are governed by the laws of {JURISDICTION}. Any
                dispute relating to the Service or these terms will be dealt
                with by the courts of {JURISDICTION}.
              </P>
            </Section>

            <Section number="15" title="Getting in touch">
              <P>
                If you have any questions about these terms, or about the
                Service generally, email us at{' '}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and
                we'll get back to you as soon as we can.
              </P>
            </Section>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}
